/**
 * pruneBodyClipTracks — strip tracks that can't (or shouldn't) bind to the rig
 * skeleton from a loaded body AnimationClip. Pure (string ops + a Set membership
 * check, no THREE import) so the pelvis .position keep/drop gate is unit testable.
 */

/**
 * @param {{tracks:Array<{name:string,times?:{length:number}}>}} clip mutated in place
 * @param {Set<string>} sceneNodes leaf node names present on the rig
 * @param {object} [opts]
 * @param {object|null} [opts.generatedMarker] the clip's aosGeneratedMotion extras
 *   (object) or null/undefined for a non-generated clip. Present with
 *   keepRootMotion !== true → the clip opted out of root motion, so strip its
 *   pelvis .position too (play in place). Present with keepRootMotion === true →
 *   keep the authored relative-travel track. Absent → pelvis .position is kept
 *   (unchanged pre-existing behavior for stock / arbitrary custom uploads).
 * @returns {{tracks:number,bound:number,active:number,unbound:number,samples:string[]}}
 */
export function pruneBodyClipTracks(clip, sceneNodes, { generatedMarker = null } = {}) {
  const STRIP_ALL = new Set(['root', 'Armature'])
  // pelvis .scale is always stripped (rig meters↔cm scale defense — a stationary
  // avatar never needs it). pelvis .position is kept by default; a generated
  // motion that opted OUT of root motion strips it too so the clip plays in place.
  const STRIP_PELVIS = new Set(['scale'])
  if (generatedMarker && generatedMarker.keepRootMotion !== true) STRIP_PELVIS.add('position')

  const ACTIVE_KEY_THRESHOLD = 10
  const original = clip.tracks.length
  const kept = []
  const unboundSamples = []
  let unbound = 0
  let active = 0
  for (const track of clip.tracks) {
    const dot      = track.name.lastIndexOf('.')
    const nodePath = dot >= 0 ? track.name.substring(0, dot) : track.name
    const prop     = dot >= 0 ? track.name.substring(dot + 1) : ''
    const leaf     = nodePath.split('/').pop()
    if (STRIP_ALL.has(leaf)) continue
    if (leaf === 'pelvis' && STRIP_PELVIS.has(prop)) continue
    if (sceneNodes.has(leaf)) {
      kept.push(track)
      if ((track.times?.length ?? 0) > ACTIVE_KEY_THRESHOLD) active++
    } else {
      unbound++
      if (unboundSamples.length < 5) unboundSamples.push(nodePath)
    }
  }
  clip.tracks = kept
  return { tracks: original, bound: kept.length, active, unbound, samples: unboundSamples }
}
